import { patchState, signalStore, withMethods, withState } from '@ngrx/signals'
import { ELocalStorageKeys, ELocationScope } from '@shared/models'

export type HubByScope = Partial<Record<ELocationScope, number | null>>

interface GlobalLocationState {
	hubByScope: HubByScope
}

function getLocalStorage(): Storage | null {
	try {
		if (!('localStorage' in globalThis)) return null
		return globalThis.localStorage
	} catch {
		return null
	}
}

function readStoredHubByScope(): HubByScope {
	const storage = getLocalStorage()
	if (!storage) return {}

	const stored = storage.getItem(ELocalStorageKeys.hubByScope)
	if (!stored) return {}

	try {
		const parsed = JSON.parse(stored) as unknown
		if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
		return parsed as HubByScope
	} catch {
		return {}
	}
}

function persistHubByScope(hubByScope: HubByScope): void {
	const storage = getLocalStorage()
	if (!storage) return

	const hasAny = Object.values(hubByScope).some((value) => value != null)
	if (!hasAny) {
		storage.removeItem(ELocalStorageKeys.hubByScope)
		return
	}

	storage.setItem(ELocalStorageKeys.hubByScope, JSON.stringify(hubByScope))
}

const initialState: GlobalLocationState = {
	hubByScope: readStoredHubByScope(),
}

export const GlobalLocationStore = signalStore(
	{ providedIn: 'root' },
	withState(initialState),
	withMethods((store) => ({
		getLocationId(scope: ELocationScope): number | null {
			const value = store.hubByScope()[scope]
			return value ?? null
		},
		setLocationId(scope: ELocationScope, locationId: number | null): void {
			const next: HubByScope = { ...store.hubByScope() }
			if (locationId == null) {
				delete next[scope]
			} else {
				next[scope] = locationId
			}
			persistHubByScope(next)
			patchState(store, { hubByScope: next })
		},
	}))
)
